import { Section } from "../tools/section";
import { CLASS_HAS_ERROR } from '../tools/constants/classes';
import { t } from '@process-creative/slate-theme-tools';

// Selectors
const SELECTOR_SECTION           = '[data-contact-form-section]';
const SELECTOR_EMAIL_FIELD       = '[data-email-field]';
const SELECTOR_ERROR_MSG         = '[data-error-message]';
const SELECTOR_SUBMIT_BTN        = '[data-submit-button]';
const SELECTOR_REQUIRED_FIELDS   = '[data-is-required]';
const SELECTOR_INPUT_WRAPPER     = '[data-input-wrapper]';

export class ContactForm extends Section {
  private emailField: HTMLInputElement | null = null;
  private errorMessage: HTMLElement | null = null;
  private submitBtn: HTMLButtonElement | null = null;
  private requiredFields: NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = null!;

  // Track touched fields (those that received focus)
  private touchedFields = new WeakSet<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>();

  queryChildren(container: HTMLElement) {
    this.emailField = container.querySelector(SELECTOR_EMAIL_FIELD);
    this.errorMessage = container.querySelector(SELECTOR_ERROR_MSG);
    this.submitBtn = container.querySelector(SELECTOR_SUBMIT_BTN);
    this.requiredFields = container.querySelectorAll(SELECTOR_REQUIRED_FIELDS);
  }

  attachEvents() {
    if (this.emailField) {
      this.emailField.addEventListener('change', this.validateEmailField.bind(this));
    }

    this.requiredFields?.forEach(field => {
      if (field !== this.emailField) {
        field.addEventListener('focus', () => this.touchedFields.add(field));
        field.addEventListener('blur', (event) => {
          if (this.touchedFields.has(field)) {
            this.validateRequiredField(event);
          }
        });
      }
    });

    this.toggleSubmitButtonState();
    
    if (this.submitBtn) {
      this.submitBtn.setAttribute('disabled', 'true');
    }
  }

  detachEvents() {
    if (this.emailField) {
      this.emailField.removeEventListener('change', this.validateEmailField.bind(this));
    }

    this.requiredFields?.forEach(field => {
      if (field !== this.emailField) {
        field.removeEventListener('focus', () => this.touchedFields.add(field));
        field.removeEventListener('blur', (event) => {
          if (this.touchedFields.has(field)) {
            this.validateRequiredField(event);
          }
        });
      }
    });
  }

  private validateEmailField(toggleSubmit: boolean = true) {
    if (!this.emailField || !this.errorMessage) return;

    const emailValue = this.emailField.value.trim();
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const inputWrapper = this.emailField.closest(SELECTOR_INPUT_WRAPPER);

    if (!emailPattern.test(emailValue)) {
      inputWrapper?.classList.add(CLASS_HAS_ERROR);
      this.errorMessage.textContent = t('general.email_err_msg');
      this.errorMessage.style.display = "block";
    } else {
      inputWrapper?.classList.remove(CLASS_HAS_ERROR);
      this.clearErrorIfNoErrors();
    }

    if (toggleSubmit) {
      this.toggleSubmitButtonState();
    }
  }

  private validateRequiredField(event: Event) {
    const field = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const value = field.value.trim();
    const inputWrapper = field.closest(SELECTOR_INPUT_WRAPPER);

    if (!value) {
      inputWrapper?.classList.add(CLASS_HAS_ERROR);

      if (this.errorMessage) {
        this.errorMessage.textContent = t('general.generic_err_msg');
        this.errorMessage.style.display = 'block';
      }
    } else {
      inputWrapper?.classList.remove(CLASS_HAS_ERROR);
      this.clearErrorIfNoErrors();
    }

    this.toggleSubmitButtonState();
  }

  private clearErrorIfNoErrors() {
    if (!this.errorMessage) return;

    const anyErrorsLeft = Array.from(this.requiredFields).some(field =>
      field.closest(SELECTOR_INPUT_WRAPPER)?.classList.contains(CLASS_HAS_ERROR)
    ) || (this.emailField?.closest(SELECTOR_INPUT_WRAPPER)?.classList.contains(CLASS_HAS_ERROR) ?? false);

    if (!anyErrorsLeft) {
      this.errorMessage.style.display = 'none';
      this.errorMessage.textContent = '';
    }
  }

  private toggleSubmitButtonState() {
    const hasErrors = Array.from(this.requiredFields).some(field => {
      const inputWrapper = field.closest(SELECTOR_INPUT_WRAPPER);
      const isEmpty = field.value.trim() === '';

      return isEmpty || inputWrapper?.classList.contains(CLASS_HAS_ERROR);
    }) || (this.emailField?.closest(SELECTOR_INPUT_WRAPPER)?.classList.contains(CLASS_HAS_ERROR) ?? false);

    if (hasErrors) {
      this.submitBtn?.setAttribute('disabled', 'true');
    } else {
      this.submitBtn?.removeAttribute('disabled');
    }
  }

}

Section.register(SELECTOR_SECTION, ContactForm);
